import { Close } from "assets/icons/Close";
import { Discord } from "assets/icons/Discord";
import ImageIcon from "assets/icons/ImageIcon";
import twitter from "assets/icons/twitter.png";
import ImagePlaceholder from "assets/images/dapps/imagePlaceholder.png";
import { Button } from "components/Button";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { dAppsAPI } from "screens/DApps/service";
import { useDAppsStore } from "screens/DApps/store";
import { ProjectAddFormValues, TEcosystem, TTag } from "screens/DApps/types";
import { ProjectFormInput } from "./ProjectFormInput";
import { TagsDropdown } from "./TagsDropdown";
import { fileApi } from "services/files";
import {
  MAX_DAPP_DESCRIPTION_LENGTH,
  MIN_IMG_FILE_SIZE
} from "screens/DApps/constants";
import { EcosystemsDropdown } from "./EcosystemsDropdown";

export const ProjectAddForm = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { isCryptoProjectLoading } = useDAppsStore();
  const [imagePreview, setImagePreview] = useState<string>(ImagePlaceholder);
  const [imageFile, setImage] = useState<File | null>(null);
  const [imageSizeError, setImageSizeError] = useState(false);

  const { register, handleSubmit, watch, reset, control, setValue, formState } =
    useForm<ProjectAddFormValues>({
      defaultValues: {
        projectName: "",
        projectDescription: "",
        tags: [],
        websiteLink: "https://",
        twitterLink: "",
        discordLink: "",
        ecosystems: []
      },
      mode: "all"
    });

  const twitterLink = watch("twitterLink");
  const image = watch("image");
  const selectedTags = watch("tags");
  const selectedEcosystems = watch("ecosystems");

  const { isAddLoading, availableTags, availableEcosystems } = useDAppsStore();

  const onSubmit: SubmitHandler<ProjectAddFormValues> = (data) => {
    dAppsAPI.createDApp(data, () => {
      setImagePreview(ImagePlaceholder);
      reset();
      onClose();
    });
  };

  const isSubmitDisabled =
    !watch("image") ||
    !watch("projectName") ||
    !watch("projectDescription") ||
    !(watch("ecosystems").length > 0) ||
    !(watch("tags").length > 0) ||
    !watch("twitterLink") ||
    !watch("websiteLink");

  const handleTagClick = (tag: TTag) => {
    const tagIndex = selectedTags.findIndex((t) => t.id === tag.id);
    if (tagIndex === -1) {
      setValue("tags", [...selectedTags, tag]);
    } else {
      setValue(
        "tags",
        selectedTags.filter((t) => t.id !== tag.id)
      );
    }
  };

  const handleEcosystemClick = (ecosystem: TEcosystem) => {
    const ecosystemIndex = selectedEcosystems.findIndex(
      (e) => e.id === ecosystem.id
    );
    if (ecosystemIndex === -1) {
      setValue("ecosystems", [...selectedEcosystems, ecosystem]);
    } else {
      setValue(
        "ecosystems",
        selectedEcosystems.filter((e) => e.id !== ecosystem.id)
      );
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > MIN_IMG_FILE_SIZE) {
        setImageSizeError(true);
        return;
      }
      setImageSizeError(false);
      setImage(file);
    }
  };

  const uploadImage = async (file: File) => {
    const url = await fileApi.uploadImage(file);
    setValue("image", url);
  };

  useEffect(() => {
    imageFile && uploadImage(imageFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageFile]);

  useEffect(() => {
    if (imageSizeError) {
      const timer = setTimeout(() => setImageSizeError(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [imageSizeError]);

  const handleCryptoProjectChange = async (twitter: string) => {
    const project = await dAppsAPI.getCryptoProjectInfo(twitter);

    if (project && project.ecosystems && project.ecosystems.length) {
      setValue("ecosystems", [...project.ecosystems]);
    }
  };

  useEffect(() => {
    if (twitterLink && availableEcosystems.length) {
      handleCryptoProjectChange(twitterLink);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twitterLink, availableEcosystems]);

  const disableButton =
    isAddLoading || isSubmitDisabled || isCryptoProjectLoading;

  return (
    <>
      {isOpen && (
        <div
          className="fixed z-4 bg-black/40 top-0 left-0 right-0 bottom-0 w-full"
          onClick={onClose}
        />
      )}
      <div
        className={`fixed w-full md:w-[487px] h-[100svh] overflow-y-auto custom-scrollbar px-[20px] md:px-[40px] pt-[60px] top-0 bottom-0 right-0 bg-black md:border-l-[1px] z-5 transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex text-[24px] font-[700] leading-[32px] justify-between items-center mb-[40px]">
          <h3>Add new project</h3>
          <button onClick={onClose}>
            <Close />
          </button>
        </div>

        <form
          id="project-add-form"
          onSubmit={handleSubmit(onSubmit)}
          className={`flex flex-col gap-[24px] h-fit`}
        >
          <div className="flex gap-[24px] w-full">
            <div className="min-w-[80px] md:min-w-[108px] max-w-[80px] md:max-w-[108px] h-[80px] md:h-[108px] rounded-full overflow-hidden custom-scrollbar flex justify-center items-center bg-[#FFFFFF1A]">
              <img
                src={image ?? imagePreview}
                alt=""
                className="w-full h-full object-cover object-center"
              />
            </div>

            <div className="flex flex-col gap-[8px] justify-center">
              <input
                id="logo-edit"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <label
                htmlFor="logo-edit"
                className="w-[190px] h-[43px] bg-[#FFFFFF1A] flex items-center justify-center rounded-full"
              >
                <button
                  type="button"
                  className="flex items-center justify-center gap-[10px] text-[14px] font-[500] leading-[18.9px]"
                  onClick={() => document.getElementById("logo-edit")?.click()}
                >
                  <ImageIcon />
                  Upload project logo*
                </button>
              </label>
              <p
                className={`text-[12px] font-[500] text-center ${
                  imageSizeError ? "text-red-500" : "opacity-70"
                }`}
              >
                {imageSizeError
                  ? "Maximum image size 5 MB"
                  : "max 5mb, .jpeg, .png"}
              </p>
            </div>
          </div>

          <ProjectFormInput
            title="Project name*"
            placeholder="Add name here"
            control={control}
            name="projectName"
          />

          <div>
            <p className="ml-1 mb-1 text-[12px] leading-[17px] font-[500] text-[#FFFFFF99]">
              Categories*
            </p>

            <TagsDropdown
              availableTags={availableTags}
              selectedTags={selectedTags}
              onTagClick={(tag) => handleTagClick(tag)}
            >
              <div className="cursor-pointer bg-[#FFFFFF1A] custom-scrollbar overflow-x-auto  overflow-y-hidden h-[43px] rounded-[8px] flex items-center gap-1 px-[20px]">
                {selectedTags.map((tag) => (
                  <span className="bg-[#202020] p-1 px-2 text-xs">
                    {tag.name}
                  </span>
                ))}
                {selectedTags.length === 0 && (
                  <span className="text-[#FFFFFF80]">Select categories</span>
                )}
              </div>
            </TagsDropdown>
          </div>

          <div>
            <p className="ml-1 mb-1 text-[12px] leading-[17px] font-[500] text-[#FFFFFF99]">
              Ecosystem*
              {isCryptoProjectLoading && (
                <span className="text-[#FFFFFF80]">
                  Loading crypto project ecosystems
                </span>
              )}
            </p>

            <EcosystemsDropdown
              availableEcosystems={availableEcosystems}
              selectedEcosystems={selectedEcosystems}
              onEcosystemClick={(ecosystem) => handleEcosystemClick(ecosystem)}
            >
              <div className="cursor-pointer bg-[#FFFFFF1A] custom-scrollbar overflow-x-auto overflow-y-hidden h-[43px] rounded-[8px] flex items-center gap-1 px-[20px]">
                {selectedEcosystems.map((ecosystem) => (
                  <span className="bg-[#202020] p-1 px-2 text-xs">
                    {ecosystem.name}
                  </span>
                ))}
                {selectedEcosystems.length === 0 && (
                  <span className="text-[#FFFFFF80]">Select ecosystems</span>
                )}
              </div>
            </EcosystemsDropdown>
          </div>

          <div className="flex flex-col gap-[4px]">
            <p className="ml-1 text-[12px] leading-[17px] font-[500] text-[#FFFFFF99]">
              Project description*
            </p>

            <div className="relative mainBannerPreview">
              <textarea
                placeholder="Add description here"
                className="bg-[#FFFFFF1A] py-[15.5px] px-[20px] rounded-[8px] flex items-center min-h-[103px] max-h-[103px] w-full text-[14px] leading-[17px] font-[500] placeholder:opacity-50 placeholder:text-[#fff] custom-scrollbar"
                style={{
                  outline: "none",
                  resize: "none",
                  border: `1px solid ${formState.errors.projectDescription ? "#FF4D4D" : "transparent"}`
                }}
                {...register("projectDescription", {
                  maxLength: {
                    message: `Max ${MAX_DAPP_DESCRIPTION_LENGTH} characters`,
                    value: MAX_DAPP_DESCRIPTION_LENGTH
                  }
                })}
              />

              <p className="absolute bottom-[12px] right-[20px] py-[4px] px-[6px] bg-[#3f3f3f] rounded-[8px] text-[#FFFFFF80]">
                {watch("projectDescription")?.length || 0}
              </p>
            </div>

            {formState.errors.projectDescription && (
              <p className="text-[12px] leading-[17px] font-[500] text-[#FF4D4D]">
                {formState.errors.projectDescription.message}
              </p>
            )}
          </div>

          <ProjectFormInput
            title="Website link"
            control={control}
            name="websiteLink"
          />

          <ProjectFormInput
            title="Twitter link*"
            placeholder="Twitter link"
            startElement={
              <img
                src={twitter}
                alt=""
                className="h-[16px] w-[16px] mr-[10px]"
              />
            }
            control={control}
            name="twitterLink"
          />
          <ProjectFormInput
            title="Discord link"
            placeholder="Discord link"
            startElement={
              <Discord width={22} height={16} className="mr-[10px]" />
            }
            control={control}
            name="discordLink"
          />
        </form>

        <div className="bg-gradient-to-t from-black to-transparent pb-[40px] pt-[60px] flex flex-col items-start w-full left-0 right-0 z-10">
          <div>* - This field is required. Fill all required fields</div>
          <Button
            extraClass="w-full"
            type="submit"
            form="project-add-form"
            disabled={disableButton}
          >
            <span className="mr-[10px] text-[18px]">+</span> Add new project
          </Button>
        </div>
      </div>
    </>
  );
};
